import * as Sentry from "@sentry/react"
import { STAGE, version } from "../../stage"
import { readUserRole } from "../users/userRole"

// Projet `welfare-monitor-web` de l'organisation Sentry `akongo-eu`, hébergée
// dans l'UE (Francfort). Le DSN est public par nature : il part dans le bundle.
const DEFAULT_DSN = "https://3a25df308bb160cec2964af60f50dd96@o4512374010085376.ingest.de.sentry.io/4512374011920464"

// Seuls les builds déployés envoient : prod et staging. Le développement local
// (`npm start`, même branché sur la prod) et les autres cibles n'envoient rien.
const ENVIRONMENT_BY_STAGE = { prod: "production", stage: "staging" }

// Bruit connu des navigateurs, sans action possible de notre côté.
const IGNORED_ERRORS = [
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",
]

const EXTENSION_URLS = [/^chrome-extension:\/\//i, /^moz-extension:\/\//i, /^safari-(web-)?extension:\/\//i]

export const sentryEnvironment = (stage = STAGE, nodeEnv = process.env.NODE_ENV) =>
  nodeEnv === "production" ? ENVIRONMENT_BY_STAGE[stage] || null : null

// Les console.log/info/debug peuvent contenir des fiches entières (animaux,
// soins, profils) : on ne garde que les avertissements et les erreurs.
export const keepBreadcrumb = (breadcrumb) => {
  if (breadcrumb?.category === "console" && !["warning", "error"].includes(breadcrumb.level)) return null
  return breadcrumb
}

let started = false

export const initSentry = ({ environment = sentryEnvironment(), env = process.env } = {}) => {
  if (started || !environment || env.REACT_APP_SENTRY_ENABLED === "false") return started
  try {
    Sentry.init({
      dsn: env.REACT_APP_SENTRY_DSN || DEFAULT_DSN,
      environment,
      release: `awm-web@${version}`,
      // Ni adresse IP, ni cookies, ni en-têtes : l'utilisateur n'est connu que
      // par son uid (cf. identifySentryUser).
      sendDefaultPii: false,
      // Erreurs seulement : pas de traces de performance ni d'enregistrement de
      // session (Replay) pour l'instant.
      ignoreErrors: IGNORED_ERRORS,
      denyUrls: EXTENSION_URLS,
      beforeBreadcrumb: keepBreadcrumb,
    })
    started = true
  } catch (_) {
    // Sentry ne doit jamais empêcher l'app de démarrer.
  }
  return started
}

export const isSentryStarted = () => started

/**
 * Rattache les erreurs suivantes à l'utilisateur connecté (uid seulement, comme
 * sur le mobile) et au parc actif. `null` à la déconnexion.
 */
export const identifySentryUser = (firebaseUser, userData, { zooId = null, demo = false } = {}) => {
  if (!started) return
  try {
    if (!firebaseUser) {
      Sentry.setUser(null)
      return
    }
    Sentry.setUser({ id: firebaseUser.uid })
    const parkId = zooId || userData?.zooId || userData?.userZoo?.value
    Sentry.setTag("zoo_id", parkId ? String(parkId) : "none")
    Sentry.setTag("role", readUserRole(userData?.userRight) || "none")
    Sentry.setTag("akongo_staff", userData?.akongoStaff === true ? "true" : "false")
    Sentry.setTag("demo", demo ? "true" : "false")
  } catch (_) {}
}

export const SentryErrorBoundary = Sentry.ErrorBoundary
